import React, { useState } from "react";
import { Route, BrowserRouter, NavLink, Routes } from 'react-router-dom';
import MicroFrontend from "./MicroFrontend";
import { 
  Layout, 
  Menu, 
  Card, 
  Avatar, 
  Row, 
  Col, 
  Statistic, 
  Button, 
  Space,
  Typography,
  Badge,
  Dropdown,
  Divider,
  Input,
  ConfigProvider,
  theme
} from 'antd';
import {
  UserOutlined,
  CalendarOutlined,
  MedicineBoxOutlined,
  BellOutlined,
  SettingOutlined,
  PlusOutlined,
  HomeOutlined,
  TeamOutlined,
  FileTextOutlined,
  ScheduleOutlined,
  ExperimentOutlined,
  MenuOutlined,
  CloseOutlined,
  DownOutlined,
  LogoutOutlined,
  UserSwitchOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  MessageOutlined,
  FileSyncOutlined,
  DashboardOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;
const { useToken } = theme;

const {
  REACT_APP_SUBAPP1_HOST: subapp1 = "http://localhost:4001/",
  REACT_APP_SUBAPP2_HOST: subapp2 = "http://localhost:4002/"
} = process.env;

const Pharmacy = ({ history }) => (
  <MicroFrontend history={history} host={subapp2} name="subapp2" />
);

const Patient = ({ history }) => (
  <MicroFrontend history={history} host={subapp1} name="subapp1" />
);

const Home = () => {
  const { token } = useToken();
  
  return (
    <Content style={{ 
      padding: window.innerWidth < 768 ? '16px' : '24px', 
      background: 'transparent',
      minHeight: 'calc(100vh - 120px)',
      margin: '0 16px' // Adjusted to remove top margin
    }}>
      {/* Welcome Header */}
      <div style={{ 
        marginBottom: '40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <Title level={2} style={{ 
            color: token.colorTextHeading, 
            marginBottom: '8px',
            fontWeight: 700,
            letterSpacing: '-0.5px'
          }}>
            Welcome Back, Dr. Roberts 👋
          </Title>
          <Text type="secondary" style={{ fontSize: '18px', lineHeight: '26px' }}>
            Here's your practice overview for today
          </Text>
        </div>
        <Input 
          placeholder="Search patients, appointments..." 
          prefix={<SearchOutlined />}
          style={{ 
            width: 300,
            borderRadius: '12px',
            background: token.colorBgElevated,
            borderColor: token.colorBorder,
            boxShadow: token.boxShadowTertiary
          }}
        />
      </div>

      {/* Dashboard Stats Row */}
      <Row gutter={[24, 32]} style={{ marginBottom: '40px' }}>
        {[
          { 
            title: 'Total Patients', 
            value: 2458, 
            change: '+12%', 
            icon: <TeamOutlined />, 
            color: token.blue6,
            bgColor: token.blue2,
          },
          { 
            title: "Today's Appointments", 
            value: 37, 
            change: '-3%', 
            icon: <CalendarOutlined />, 
            color: token.green6,
            bgColor: token.green2,
          },
          { 
            title: 'Pending Consultations', 
            value: 12, 
            change: '-8%', 
            icon: <MedicineBoxOutlined />, 
            color: token.orange6,
            bgColor: token.orange2
          },
          { 
            title: 'Unread Messages', 
            value: 5, 
            change: '+2%', 
            icon: <MessageOutlined />, 
            color: token.purple6,
            bgColor: token.purple2
          }
        ].map((stat, index) => (
          <Col key={index} xs={24} sm={12} md={6}>
            <Card 
              bordered={false} 
              style={{ 
                borderRadius: '12px', 
                boxShadow: token.boxShadowTertiary,
                height: '100%',
                background: token.colorBgContainer,
                border: `1px solid ${token.colorBorderSecondary}`
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <Text style={{ 
                    fontSize: '14px',
                    fontWeight: 500,
                    color: stat.color
                  }}>
                    {stat.title}
                  </Text>
                  <Title level={2} style={{ 
                    margin: '8px 0',
                    color: token.colorTextHeading,
                    fontWeight: 800
                  }}>
                    {stat.value}
                  </Title>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Text style={{ 
                      color: stat.change.startsWith('+') ? token.green6 : token.red6, 
                      marginRight: '8px',
                      fontWeight: 600
                    }}>
                      {stat.change}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      vs last month
                    </Text>
                  </div>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: '#fff',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
                }}>
                  {React.cloneElement(stat.icon, { 
                    style: { 
                      color: stat.color,
                      fontSize: '22px',
                    } 
                  })}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Main Content Area */}
      <Row gutter={[24, 32]}>
        {/* Recent Patients Card */}
        <Col xs={24} lg={12}>
          <Card 
            bordered={false} 
            style={{ 
              borderRadius: '12px', 
              boxShadow: token.boxShadowTertiary,
              height: '100%',
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`
            }}
            title={
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '8px 0'
              }}>
                <Title level={5} style={{ 
                  color: token.colorTextHeading, 
                  margin: 0,
                  fontWeight: 700,
                  fontSize: '16px'
                }}>
                  Recent Patients
                </Title>
                <Button 
                  type="link" 
                  size="small"
                  style={{ 
                    color: token.colorPrimary,
                    fontWeight: 500,
                    padding: '0',
                    transition: 'all 0.inositol3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = token.colorPrimaryHover}
                  onMouseLeave={(e) => e.currentTarget.style.color = token.colorPrimary}
                >
                  View All
                </Button>
              </div>
            }
            headStyle={{ 
              borderBottom: `1px solid ${token.colorBorderSecondary}`, 
              padding: '0 24px',
              background: token.colorBgContainer 
            }}
            bodyStyle={{ padding: '12px 0' }}
          >
            {[
              { name: 'Viral Gautami', lastVisit: '2 Agust', status: 'Follow-up', avatarColor: token.blue6 },
              { name: 'Michael Chen', lastVisit: 'Yesterday', status: 'New Patient', avatarColor: token.green6 },
              { name: 'Emma Wilson', lastVisit: '2 days ago', status: 'Consultation', avatarColor: token.orange6 },
              { name: 'James Rodriguez', lastVisit: '1 week ago', status: 'Routine Check', avatarColor: token.purple6 },
              { name: 'Olivia Martinez', lastVisit: '2 weeks ago', status: 'Follow-up', avatarColor: token.red6 }
            ].map((patient, index) => (
              <div 
                key={index} 
                style={{ 
                  padding: '16px 24px',
                  borderRadius: '8px',
                  margin: '4px 0',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  ':hover': {
                    background: token.controlItemBgHover
                  }
                }}
              >
                <Avatar 
                  style={{ 
                    background: `linear-gradient(135deg, ${patient.avatarColor} 0%, ${token.colorPrimaryHover} 100%)`,
                    color: 'white',
                    marginRight: '16px',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                  size="large"
                >
                  {patient.name.charAt(0)}
                </Avatar>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ 
                    display: 'block', 
                    fontSize: '15px',
                    color: token.colorTextHeading
                  }}>
                    {patient.name}
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '13px' }}>
                      Last visit: {patient.lastVisit}
                    </Text>
                    <Badge 
                      color={patient.status === 'New Patient' ? 'green' : 'blue'} 
                      text={
                        <Text style={{ 
                          fontSize: '12px',
                          color: patient.status === 'New Patient' ? token.green6 : token.blue6,
                          fontWeight: 500
                        }}>
                          {patient.status}
                        </Text>
                      } 
                    />
                  </div>
                </div>
                <Button 
                  type="text" 
                  shape="circle" 
                  icon={<UserOutlined style={{ color: token.colorTextSecondary }} />} 
                  style={{ width: '32px', height: '32px' }}
                />
              </div>
            ))}
          </Card>
        </Col>

        {/* Upcoming Appointments */}
        <Col xs={24} lg={12}>
          <Card 
            bordered={false} 
            style={{ 
              borderRadius: '12px', 
              boxShadow: token.boxShadowTertiary,
              height: '100%',
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`
            }}
            title={
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '8px 0'
              }}>
                <Title level={5} style={{ 
                  color: token.colorTextHeading, 
                  margin: 0,
                  fontWeight: 700,
                  fontSize: '16px'
                }}>
                  Upcoming Appointments
                </Title>
                <Button 
                  type="link" 
                  size="small"
                  style={{ 
                    color: token.colorPrimary,
                    fontWeight: 500,
                    padding: '0',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = token.colorPrimaryHover}
                  onMouseLeave={(e) => e.currentTarget.style.color = token.colorPrimary}
                >
                  View Calendar
                </Button>
              </div>
            }
            headStyle={{ 
              borderBottom: `1px solid ${token.colorBorderSecondary}`, 
              padding: '0 24px',
              background: token.colorBgContainer 
            }}
            bodyStyle={{ padding: '12px 0' }}
          >
            {[
              { 
                time: '2:30 PM', 
                patient: 'Sarah Johnson', 
                type: 'Follow-up', 
                urgent: true,
                duration: '30 mins',
                avatarColor: token.blue6
              },
              { 
                time: '4:15 PM', 
                patient: 'Robert Lewis', 
                type: 'Lab Results', 
                urgent: false,
                duration: '45 mins',
                avatarColor: token.green6
              },
              { 
                time: '5:00 PM', 
                patient: 'Linda Thompson', 
                type: 'Consultation', 
                urgent: true,
                duration: '1 hour',
                avatarColor: token.orange6
              },
              { 
                time: '9:30 AM', 
                patient: 'David Kim', 
                type: 'Annual Checkup', 
                urgent: false,
                duration: '1 hour',
                avatarColor: token.purple6
              }
            ].map((appt, index) => (
              <div 
                key={index} 
                style={{ 
                  padding: '16px 24px',
                  borderRadius: '8px',
                  margin: '4px 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  ':hover': {
                    background: token.controlItemBgHover
                  }
                }}
              >
                <Space size="middle">
                  <Avatar 
                    size="large"
                    style={{ 
                      background: `linear-gradient(135deg, ${appt.avatarColor} 0%, ${token.colorPrimaryHover} 100%)`,
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  >
                    {appt.patient.charAt(0)}
                  </Avatar>
                  <div>
                    <Text strong style={{ 
                      display: 'block', 
                      fontSize: '15px',
                      color: token.colorTextHeading
                    }}>
                      {appt.patient}
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '13px' }}>
                        {appt.time} • {appt.duration}
                      </Text>
                      <Badge 
                        color={appt.type === 'Follow-up' ? 'blue' : 'orange'} 
                        text={
                          <Text style={{ 
                            fontSize: '12px',
                            color: appt.type === 'Follow-up' ? token.blue6 : token.orange6,
                            fontWeight: 500
                          }}>
                            {appt.type}
                          </Text>
                        } 
                      />
                    </div>
                  </div>
                </Space>
                <Space>
                  {appt.urgent && (
                    <Badge 
                      color="red" 
                      text={
                        <Text style={{ 
                          fontSize: '12px',
                          fontWeight: 600,
                          color: token.red6
                        }}>
                          Urgent
                        </Text>
                      } 
                    />
                  )}
                  <Button 
                    type="primary" 
                    size="small" 
                    shape="round"
                    style={{ transition: 'all 0.3s' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = token.colorPrimaryHover}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = token.colorPrimary}
                  >
                    Details
                  </Button>
                </Space>
              </div>
            ))}
          </Card>
        </Col>
      </Row>
    </Content>
  );
};

const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [activeMenuKey, setActiveMenuKey] = useState('1');
  const { token } = useToken();
  
  const userMenu = (
    <Menu
      items={[
        {
          key: '0',
          icon: <UserOutlined style={{ color: token.colorTextSecondary }} />,
          label: 'My Profile'
        },
        {
          key: '1',
          icon: <SettingOutlined style={{ color: token.colorTextSecondary }} />,
          label: 'Settings'
        },
        {
          key: '2',
          icon: <QuestionCircleOutlined style={{ color: token.colorTextSecondary }} />,
          label: 'Help Center'
        },
        {
          type: 'divider',
        },
        {
          key: '3',
          icon: <LogoutOutlined style={{ color: token.colorError }} />,
          label: 'Logout',
          danger: true
        }
      ]}
    />
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#3b82f6',
          borderRadius: 12,
          colorBgContainer: '#ffffff',
          colorBgLayout: '#f8fafc',
          colorBgElevated: '#ffffff',
          colorBorder: '#e2e8f0',
          colorBorderSecondary: '#f1f5f9',
          colorText: '#1e293b',
          colorTextHeading: '#0f172a',
          colorTextSecondary: '#64748b',
          colorFillSecondary: '#f1f5f9',
        },
        components: {
          Layout: {
            headerBg: '#ffffff',
            bodyBg: '#f8fafc',
            siderBg: '#ffffff',
          },
          Menu: {
            itemHoverBg: 'rgba(59, 130, 246, 0.08)',
            itemSelectedBg: 'rgba(59, 130, 246, 0.1)',
            itemSelectedColor: '#3b82f6',
            itemHoverColor: '#3b82f6',
            transition: 'all 0.3s'
          },
          Card: {
            colorBgContainer: '#ffffff',
            colorBorderSecondary: '#f1f5f9',
          },
          Button: {
            colorBgContainer: '#ffffff',
            defaultBorderColor: '#e2e8f0',
          },
          Dropdown: {
            colorBgElevated: '#ffffff',
          }
        },
      }}
    >
      <BrowserRouter>
        <Layout style={{ 
          minHeight: '100vh',
          background: token.colorBgLayout
        }}>
          {/* Sidebar */}
          <Sider
            width={280}
            style={{
              background: token.colorBgContainer,
              boxShadow: token.boxShadowSecondary,
              position: 'fixed',
              height: '100vh',
              zIndex: 100,
              left: 0,
              top: 0,
              bottom: 0,
              borderRight: `1px solid ${token.colorBorderSecondary}`
            }}
            breakpoint="lg"
            collapsedWidth="0"
            collapsible
            collapsed={collapsed}
            onCollapse={(value) => setCollapsed(value)}
            trigger={null}
          >
            <div style={{
              padding: '24px 24px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '8px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #93c5fd 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '18px',
                boxShadow: '0 4px 6px rgba(59, 130, 246, 0.25)'
              }}>
                <MedicineBoxOutlined style={{ fontSize: '20px' }} />
              </div>
              <Title level={4} style={{ 
                color: token.colorTextHeading, 
                margin: 0,
                fontWeight: 700,
                letterSpacing: '-0.5px'
              }}>
                Shushtology
              </Title>
            </div>

            <Menu
              mode="inline"
              selectedKeys={[activeMenuKey]}
              style={{ 
                background: 'transparent', 
                borderRight: 'none',
                padding: '12px'
              }}
              onSelect={({ key }) => setActiveMenuKey(key)}
            >
              {[
                { 
                  key: '1',
                  icon: <DashboardOutlined style={{ fontSize: '20px' }} />,
                  label: 'Dashboard',
                  path: '/home'
                },
                { 
                  key: '2',
                  icon: <UserOutlined style={{ fontSize: '20px' }} />,
                  label: 'Patient Records',
                  path: '/patient'
                },
                { 
                  key: '3',
                  icon: <MedicineBoxOutlined style={{ fontSize: '20px' }} />,
                  label: 'Pharmacy Portal',
                  path: '/pharmacy'
                }
              ].map(item => (
                <Menu.Item 
                  key={item.key}
                  icon={item.icon}
                  style={{
                    height: '48px',
                    margin: '6px 0',
                    borderRadius: '8px',
                    fontWeight: 500
                  }}
                >
                  <NavLink 
                    to={item.path} 
                    style={{ 
                      fontSize: '15px',
                      color: activeMenuKey === item.key ? token.colorPrimary : token.colorText
                    }}
                  >
                    {item.label}
                  </NavLink>
                </Menu.Item>
              ))}
            </Menu>
            
            {/* User profile section */}
            <div style={{
              position: 'fixed',
              bottom: '32px',
              left: '16px',
              width: '248px',
              padding: '20px',
              borderRadius: '12px',
              background: token.colorBgElevated,
              border: `1px solid ${token.colorBorderSecondary}`,
              boxShadow: token.boxShadowTertiary
            }}>
              <Space>
                <Avatar 
                  size="large"
                  style={{ 
                    background: 'linear-gradient(135deg, #3b82f6 0%, #93c5fd 100%)',
                    color: 'white',
                    boxShadow: '0 4px 6px rgba(59, 130, 246, 0.25)'
                  }} 
                >
                  DR
                </Avatar>
                <div>
                  <Text strong style={{ 
                    color: token.colorTextHeading, 
                    display: 'block', 
                    fontSize: '15px'
                  }}>
                    Dr. Roberts
                  </Text>
                  <Text style={{ 
                    color: token.colorTextSecondary, 
                    fontSize: '12px'
                  }}>
                    Administrator
                  </Text>
                </div>
              </Space>
            </div>
          </Sider>

          {/* Main Content Area */}
          <Layout style={{ 
            marginLeft: collapsed ? 0 : 280,
            transition: 'margin 0.2s',
            background: token.colorBgLayout
          }}>
            <Header style={{ 
              background: token.colorBgContainer,
              padding: '0 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: token.boxShadowTertiary,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              height: '72px',
              borderBottom: `1px solid ${token.colorBorderSecondary}`
            }}>
              {/* Left side - Collapse button */}
              <Space>
                <Button 
                  type="text" 
                  icon={collapsed ? 
                    <MenuUnfoldOutlined style={{ fontSize: '18px', color: token.colorText }} /> : 
                    <MenuFoldOutlined style={{ fontSize: '18px', color: token.colorText }} />
                  }
                  onClick={() => setCollapsed(!collapsed)}
                  style={{ 
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                  }}
                />
                
                <Title level={4} style={{ 
                  margin: 0, 
                  color: token.colorPrimary,
                  fontWeight: 600,
                  letterSpacing: '-0.5px'
                }}>
                  Shushtology : A Cloud Computing Platform for Healthcare
                </Title>
              </Space>
              
              {/* Right side - Controls */}
              <Space size="middle">
                  <Button 
                    type="text" 
                    icon={<BellOutlined style={{ fontSize: '18px', color: token.colorText }} />} 
                    style={{ 
                      width: '48px',
                      height: '48px',
                      borderRadius: '8px',
                    }}
                  />
                
                <Dropdown 
                  overlay={userMenu} 
                  trigger={['click']}
                  placement="bottomRight"
                  overlayStyle={{
                    minWidth: '200px',
                    borderRadius: '12px',
                    boxShadow: token.boxShadowSecondary,
                    border: `1px solid ${token.colorBorderSecondary}`
                  }}
                >
                  <Button 
                    type="text" 
                    style={{ 
                      padding: '4px 8px',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: '8px',
                      background: 'transparent'
                    }}
                  >
                    <Avatar 
                      size="default" 
                      style={{ 
                        background: 'linear-gradient(135deg, #3b82f6 0%, #93c5fd 100%)',
                        color: 'white',
                        marginRight: '8px',
                        boxShadow: '0 4px 6px rgba(59, 130, 246, 0.25)'
                      }} 
                    >
                      DR
                    </Avatar>
                    <div style={{ textAlign: 'left', marginRight: '8px' }}>
                      <Text strong style={{ 
                        display: 'block',
                        color: token.colorText,
                        fontSize: '14px'
                      }}>
                        Dr. Roberts
                      </Text>
                      <Text style={{ 
                        color: token.colorTextSecondary,
                        fontSize: '12px'
                      }}>
                        Admin
                      </Text>
                    </div>
                    <DownOutlined style={{ 
                      fontSize: '12px',
                      color: token.colorText
                    }} />
                  </Button>
                </Dropdown>
              </Space>
            </Header>
            
            <Content style={{ 
              margin: '0 16px',
              overflow: 'initial',
              padding: window.innerWidth < 768 ? '16px' : '24px',
              background: token.colorBgLayout,
              minHeight: 'calc(100vh - 120px)'
            }}>
              <Routes>
                <Route path="/" element={<Home />} /> 
                <Route path="/home" element={<Home />} />
                <Route path="/patient" element={<Patient />} />
                <Route path="/pharmacy" element={<Pharmacy />} />
              </Routes>
            </Content>
          </Layout>
        </Layout>
      </BrowserRouter>
    </ConfigProvider>
  );
};

export default App;